// Story 063 (D-6) — single-surface tokens for widget pop-out window
// dimensions. Tunable here instead of grepping through call sites.
//
// Starting values come from the 12.01.2026 meeting + Christian's
// 2026-05-15 annotation:
//   - Viewer is the analyst's central surface — full-HD-ish (1600×900).
//   - Tiles / Defects list / Filmstrip favour a tall portrait so the
//     analyst can park them on a second monitor and scroll.
//   - Defect catalog and Navigator are tight popovers — small.
//   - Sketch is a wide landscape (component overview).

export type PopoutWidgetKey =
  | "viewer"
  | "tiles"
  | "list"
  | "sketch"
  | "filmstrip"
  | "navigator"
  | "catalogue"
  // SB6-ANA-POP-04 — the two advanced filter widgets are now poppable too,
  // so an analyst can park them on a third screen and keep driving the main
  // analysis. Their committed sub-filter (AI `aiClassFilter`) + the global
  // selection (`defectTypeIds`) cross windows via the filter sync channel.
  | "aiFilter"
  | "defectFilter"
  // Story 056 — the image histogram; recomputes off the shared image
  // activation, which already crosses windows via the viewer sync channel.
  | "histogram";

export interface WindowSize {
  readonly width: number;
  readonly height: number;
}

export const WIDGET_DEFAULT_WINDOW_SIZE: Readonly<
  Record<PopoutWidgetKey, WindowSize>
> = {
  viewer: { width: 1600, height: 900 },
  tiles: { width: 400, height: 900 },
  list: { width: 500, height: 900 },
  sketch: { width: 700, height: 500 },
  filmstrip: { width: 280, height: 800 },
  navigator: { width: 350, height: 600 },
  catalogue: { width: 350, height: 600 },
  // SB6-ANA-POP-04 (D3) — tall portrait tables; opened a touch wider so the
  // confidence slider + numeric column breathe on a third screen.
  aiFilter: { width: 760, height: 900 },
  defectFilter: { width: 760, height: 900 },
  // Story 056 — a compact landscape chart window.
  histogram: { width: 560, height: 420 },
};

/** Friendly label used in the Settings drawer "Multi-window layout"
 *  section and the pop-out window title. Kept here so the rename
 *  surface is single-file. */
export const WIDGET_LABEL: Readonly<Record<PopoutWidgetKey, string>> = {
  viewer: "Viewer",
  tiles: "Tiles",
  list: "Defects · AI Findings",
  sketch: "Component",
  filmstrip: "Filmstrip",
  navigator: "Filters",
  catalogue: "Defect catalogue",
  aiFilter: "AI Finding Filter",
  defectFilter: "Defect Filter",
  histogram: "Histogram",
};

/** Viewport width below which the pop-out action is hidden — analysts
 *  work at desks (mobile/tablet fallback per AC8). */
export const MIN_POPOUT_VIEWPORT_PX = 1024;
